/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { getAllRaiseInvoices } from "services/financeService";

// @mui material components
import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import PaginationBar from "components/PaginationBar/PaginationBar";
// Data
// import ClientBalanceTableData from "Views/FinanceManagment/ClientBalance/data/ClientBalanceTableData"

function Tables() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 }; // Fetch more data for client-side pagination
      const response = await getAllRaiseInvoices(params);
      if (response.success) {
        setInvoices(response.data);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedInvoices = invoices.slice(startIndex, endIndex);
  const totalPages = Math.ceil(invoices.length / rowsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const columns = [
    { name: "sno", align: "center" },
    { name: "Name", align: "left" },
    { name: "Total paid Amount", align: "center" },
    { name: "Balance Amount", align: "center" },
  ];

  const rows = paginatedInvoices.map((invoice, index) => ({
    sno: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {startIndex + index + 1}
      </ArgonTypography>
    ),
    Name: (
      <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
        <ArgonBox mr={2}>
          <ArgonAvatar
            src={invoice.parentId?.profileImage || invoice.tutorId?.profileImage || ""}
            alt={invoice.parentId?.fullName || "N/A"}
            size="sm"
            variant="full"
          />
        </ArgonBox>
        <ArgonBox display="flex" flexDirection="column">
          <ArgonTypography variant="button" fontWeight="medium">
            {invoice.parentId?.fullName || "N/A"}
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary">
            {invoice.parentId?.email || invoice.tutorId?.email || "N/A"}
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary">
            {invoice.parentId?.phone || "N/A"}
          </ArgonTypography>
        </ArgonBox>
      </ArgonBox>
    ),
    "Total paid Amount": (
      <ArgonTypography variant="caption" color="success" fontWeight="medium">
        ₹{invoice.totalAmount || 0}
      </ArgonTypography>
    ),
    "Balance Amount": (
      <ArgonTypography variant="caption" color="warning" fontWeight="medium">
        ₹{(invoice.totalAmount || 0) - (invoice.attendanceTotalAmount || 0)}
      </ArgonTypography>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Client Balance
              </ArgonTypography>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching Client Balance...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : invoices.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    No Client Balance found
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Try adjusting your filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
            {invoices.length > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={invoices.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
